import { useCallback, useRef, useState } from 'react';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import {
  type AudioRecorderErrorReason,
  useAudioRecorder,
} from '@backrs_ui/common-ui/src/hooks/useAudioRecorder';
import { AttachedFileChip } from '@backrs_ui/common-ui/src/molecules/Lubav/AttachedFileChip';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  RichTextEditor,
  type RichTextEditorHandle,
} from '@backrs_ui/grow-ui/src/components/RichTextEditor/RichTextEditor';
import {
  type AutosaveStatus,
  useDebouncedAutosave,
} from '@backrs_ui/grow-ui/src/hooks/useDebouncedAutosave';
import {
  attachmentsToChips,
  buildNoteSavePayload,
  registryEntriesFromBody,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/CoachNotes/coachNoteDraft';
import { isNoteBodyEmpty } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/CoachNotes/coachNotes.utils';
import {
  MAX_ATTACHMENTS,
  useNoteAttachments,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/CoachNotes/useNoteAttachments';
import { transcribeAudio } from '@backrs_ui/grow-ui/src/redux/coachLubav/transcribeAudio';
import type {
  ChipAttachment,
  RegistryEntry,
} from '@backrs_ui/grow-ui/src/redux/coachNoteEditor/coachNoteEditor';
import { selectIsEmulating } from '@backrs_ui/grow-ui/src/redux/emulator/emulator';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useGetTaskReflectionQuery,
  usePutTaskReflectionMutation,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import type { PriorityTaskReflection } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';
import { isImageMime } from '@backrs_ui/grow-ui/src/utils/uploadFile';

import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

const STRINGS = appStrings.modules.priorities.taskPage.reflection;
const RECORDING_ERRORS = commonAppStrings.modules.audioRecording.errors;

const MAX_DICTATION_MS = 2 * 60 * 1000;

type ReflectionBody = PriorityTaskReflection['body'];

type ReflectionDraft = Readonly<{
  body: ReflectionBody;
  chips: readonly ChipAttachment[];
  registry: readonly RegistryEntry[];
}>;

type TaskReflectionProps = Readonly<{
  taskId: number;
}>;

export const TaskReflection = ({ taskId }: TaskReflectionProps) => {
  const isEmulating = useAppSelector(selectIsEmulating);
  // Not `data`: it keeps the previous task's reflection while this one loads.
  const { currentData: reflection, isError } = useGetTaskReflectionQuery(taskId);

  const renderContent = () => {
    if (isError) {
      return (
        <Typography variant="text-sm-regular" sx={errorTextSx}>
          {STRINGS.loadError}
        </Typography>
      );
    }

    if (reflection === undefined) return <CircularProgress size={20} />;

    if (isEmulating) return <ReadOnlyReflection reflection={reflection} />;

    return <ReflectionEditor key={taskId} taskId={taskId} reflection={reflection} />;
  };

  return (
    <Box id="task-reflection" sx={cardSx}>
      <Box sx={headerSx}>
        <Typography variant="text-md-semibold" sx={headingSx}>
          {STRINGS.heading}
        </Typography>
      </Box>
      <Box sx={contentSx}>{renderContent()}</Box>
    </Box>
  );
};

type ReflectionEditorProps = Readonly<{
  taskId: number;
  reflection: PriorityTaskReflection | null;
}>;

const ReflectionEditor = ({ taskId, reflection }: ReflectionEditorProps) => {
  const [putTaskReflection] = usePutTaskReflectionMutation();
  // Saves write their response into the query cache, so this covers new uploads too.
  const attachmentUris = new Map(
    (reflection?.attachments ?? []).map((attachment) => [attachment.id, attachment.uri]),
  );
  const [initialValue] = useState(reflection?.body ?? null);
  const [chips, setChips] = useState<ChipAttachment[]>(() =>
    attachmentsToChips(reflection?.attachments),
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [body, setBody] = useState<ReflectionBody | null>(reflection?.body ?? null);

  const bodyRef = useRef<ReflectionBody | null>(reflection?.body ?? null);
  const chipsRef = useRef(chips);
  const registryRef = useRef(new Map(registryEntriesFromBody(reflection?.body ?? null)));
  // The editor reports focus and selection changes as updates too; only content
  // that differs from the last seen state is saved.
  const lastBodyJsonRef = useRef(reflection ? JSON.stringify(reflection.body) : null);
  const editorControlRef = useRef<RichTextEditorHandle | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const attachInputRef = useRef<HTMLInputElement | null>(null);

  const save = useCallback(
    async (draft: ReflectionDraft) => {
      const payload = buildNoteSavePayload(draft.body, draft.chips, draft.registry);

      if (!payload) return false;

      await putTaskReflection({ taskId, ...payload }).unwrap();
    },
    [putTaskReflection, taskId],
  );

  const { status, schedule } = useDebouncedAutosave({ save });

  const scheduleDraft = useCallback(() => {
    if (!bodyRef.current) return;

    schedule({
      body: bodyRef.current,
      chips: chipsRef.current,
      registry: Array.from(registryRef.current),
    });
  }, [schedule]);

  const replaceChips = useCallback((next: ChipAttachment[]) => {
    chipsRef.current = next;
    setChips(next);
  }, []);

  const attachments = useNoteAttachments({
    body,
    chips,
    onAddChip: (chip) => replaceChips([...chipsRef.current, chip]),
    onUpdateChip: (chip) => {
      replaceChips(chipsRef.current.map((c) => (c.localId === chip.localId ? chip : c)));
      if (chip.status === 'done') scheduleDraft();
    },
    onRemoveChip: (localId) => {
      replaceChips(chipsRef.current.filter((c) => c.localId !== localId));
      scheduleDraft();
    },
    onAddRegistryEntry: ([src, mediaId]) => {
      registryRef.current.set(src, mediaId);
    },
  });

  const handleTranscript = useCallback((text: string) => {
    editorControlRef.current?.insertText(text);
  }, []);

  const handleRecordingError = useCallback((reason: AudioRecorderErrorReason) => {
    setValidationError(RECORDING_ERRORS[reason]);
  }, []);

  const {
    status: dictationStatus,
    startRecording,
    stopRecording,
  } = useAudioRecorder({
    transcribe: transcribeAudio,
    onTranscript: handleTranscript,
    onError: handleRecordingError,
    maxDurationMs: MAX_DICTATION_MS,
  });

  const handleStartRecording = useCallback(() => {
    setValidationError(null);
    void startRecording();
  }, [startRecording]);

  const handleChange = useCallback(
    (next: ReflectionBody) => {
      const nextJson = JSON.stringify(next);

      bodyRef.current = next;

      // An untouched editor on a task without a reflection must not create one.
      if (lastBodyJsonRef.current === null && isNoteBodyEmpty(next)) {
        lastBodyJsonRef.current = nextJson;

        return;
      }

      if (nextJson === lastBodyJsonRef.current) return;

      lastBodyJsonRef.current = nextJson;
      setBody(next);
      scheduleDraft();
    },
    [scheduleDraft],
  );

  const handleFileSelected =
    (kind: 'image' | 'attach') => async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      event.target.value = '';
      if (!file) return;

      const isImage = isImageMime(file.type);

      if (kind === 'image' && !isImage) {
        setValidationError(STRINGS.imageTypeError);

        return;
      }

      if (kind === 'attach' && isImage) {
        setValidationError(STRINGS.attachNonImageError);

        return;
      }

      setValidationError(null);

      if (kind === 'image') {
        const result = await attachments.uploadImage(file);

        if (!result.ok) {
          setValidationError(
            result.reason === 'too_large' ? STRINGS.fileTooLargeError : STRINGS.uploadError,
          );

          return;
        }

        editorControlRef.current?.insertImage(result.src, result.alt);
      } else {
        const result = await attachments.uploadAttachment(file);

        if (!result.ok) setValidationError(STRINGS.fileTooLargeError);
      }
    };

  return (
    <Box sx={editorWrapSx}>
      <RichTextEditor
        id="task-reflection-editor"
        initialValue={initialValue}
        onChange={handleChange}
        placeholder={STRINGS.placeholder}
        onInsertImage={() => imageInputRef.current?.click()}
        onAttachFile={() => attachInputRef.current?.click()}
        attachmentSlotsRemaining={attachments.slotsRemaining}
        dictation={{
          status: dictationStatus,
          onStart: handleStartRecording,
          onStop: stopRecording,
        }}
        controlRef={editorControlRef}
        openableLinks
      />
      <input
        id="task-reflection-image-input"
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        onChange={handleFileSelected('image')}
      />
      <input
        id="task-reflection-attach-input"
        ref={attachInputRef}
        type="file"
        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        hidden
        onChange={handleFileSelected('attach')}
      />
      {(chips.length > 0 || attachments.slotsUsed > 0) && (
        <Box sx={attachmentRowSx}>
          {chips.length > 0 && (
            <Stack direction="row" flexWrap="wrap" sx={chipRowSx}>
              {chips.map((chip) => (
                <FileChip
                  key={chip.localId}
                  id={`task-reflection-attachment-${chip.localId}`}
                  filename={chip.filename}
                  status={chip.status}
                  uri={chip.status === 'done' ? attachmentUris.get(chip.mediaId) : undefined}
                  onRemove={() => attachments.removeChip(chip.localId)}
                />
              ))}
            </Stack>
          )}
          {attachments.slotsUsed > 0 && (
            <Typography variant="text-xs-regular" sx={mutedTextSx}>
              {STRINGS.attachmentSlotsHint(attachments.slotsUsed, MAX_ATTACHMENTS)}
            </Typography>
          )}
        </Box>
      )}
      {attachments.isUploading && <LinearProgress />}
      {validationError && (
        <Alert severity="error" onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}
      <SaveStatusLabel status={status} />
    </Box>
  );
};

const ReadOnlyReflection = ({
  reflection,
}: Readonly<{ reflection: PriorityTaskReflection | null }>) => {
  const files = (reflection?.attachments ?? []).filter((attachment) => !attachment.is_inline);
  const hasText = !!reflection && !isNoteBodyEmpty(reflection.body);

  if (!reflection || (!hasText && files.length === 0)) {
    return (
      <Typography variant="text-md-regular" sx={mutedTextSx}>
        {STRINGS.empty}
      </Typography>
    );
  }

  return (
    <Box sx={editorWrapSx}>
      <RichTextEditor id="task-reflection-readonly" initialValue={reflection.body} readOnly />
      {files.length > 0 && (
        <Stack direction="row" flexWrap="wrap" sx={chipRowSx}>
          {files.map((file) => (
            <Box
              key={file.id}
              component="a"
              id={`task-reflection-readonly-attachment-${file.id}`}
              href={file.uri}
              target="_blank"
              rel="noopener noreferrer"
              sx={attachmentLinkSx}
            >
              <AttachedFileChip filename={file.name} status="done" />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

type FileChipProps = Readonly<{
  id: string;
  filename: string;
  status: ChipAttachment['status'];
  /** Signed URL; the chip opens it in a new tab when present. */
  uri?: string;
  onRemove: () => void;
}>;

const FileChip = ({ id, filename, status, uri, onRemove }: FileChipProps) => {
  const openFile = () => uri && window.open(uri, '_blank', 'noopener,noreferrer');

  return (
    <Box
      id={id}
      role={uri ? 'link' : undefined}
      tabIndex={uri ? 0 : undefined}
      onClick={openFile}
      onKeyDown={(event) => {
        if (event.key === 'Enter') openFile();
      }}
      sx={uri ? openableChipSx : undefined}
    >
      <AttachedFileChip filename={filename} status={status} onRemove={onRemove} />
    </Box>
  );
};

const SAVE_STATUS_TEXT: Record<AutosaveStatus, string | null> = {
  idle: null,
  saving: STRINGS.saving,
  saved: STRINGS.saved,
  error: STRINGS.saveError,
};

const SaveStatusLabel = ({ status }: Readonly<{ status: AutosaveStatus }>) => {
  const text = SAVE_STATUS_TEXT[status];

  return (
    <Box sx={statusRowSx} aria-live="polite" id="task-reflection-save-status">
      {status === 'saving' && <CircularProgress size={12} sx={spinnerSx} />}
      {text && (
        <Typography variant="text-xs-regular" sx={status === 'error' ? errorTextSx : mutedTextSx}>
          {text}
        </Typography>
      )}
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
  border: `1px solid ${backrs.colors['Gray/500']}`,
  borderRadius: backrs.radius['radius-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-xl'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-3xl'],
});

const headingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const editorWrapSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  '& #task-reflection-editor .rt-content': {
    padding: backrs.spacing['spacing-2xl'],
    border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    borderRadius: backrs.radius['radius-xl'],
    backgroundColor: backrs.colors['Beige/25'],
  },
});

const attachmentRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const chipRowSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-sm'],
});

const openableChipSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const attachmentLinkSx: SxProps<Theme> = {
  textDecoration: 'none',
  display: 'inline-flex',
};

const statusRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-xs'],
});

const spinnerSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/500'],
});

const mutedTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const errorTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});
